import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  GraduationCap, Search, Printer, BookOpen, ChevronLeft, ChevronRight, X,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { HELP_GROUPS, topicById, ALL_TOPICS } from '../help/topics'
import { GLOSSARY, glossSlug } from '../help/glossary'

/* ── glossary view (the 'glossary' topic renders this) ──────────────────── */

function GlossaryView() {
  const [sp] = useSearchParams()
  const want = (sp.get('term') || '').toLowerCase()
  const [filter, setFilter] = useState('')
  const [flash, setFlash] = useState('')

  const entries = useMemo(() => {
    const f = filter.toLowerCase().trim()
    const list = f
      ? GLOSSARY.filter((e) => `${e.term} ${e.aka || ''} ${e.def}`.toLowerCase().includes(f))
      : GLOSSARY
    return [...list].sort((a, b) => a.term.localeCompare(b.term))
  }, [filter])

  const byLetter = useMemo(() => {
    const m = new Map()
    for (const e of entries) {
      const l = e.term[0].toUpperCase()
      if (!m.has(l)) m.set(l, [])
      m.get(l).push(e)
    }
    return [...m.entries()]
  }, [entries])

  // deep link: /help/glossary?term=IRN → scroll + highlight
  useEffect(() => {
    if (!want) return
    const hit = GLOSSARY.find((e) =>
      e.term.toLowerCase() === want || glossSlug(e.term) === glossSlug(want) ||
      (e.aka || '').toLowerCase().includes(want))
    if (!hit) { setFilter(want); return }
    setFlash(hit.term)
    const t = setTimeout(() => {
      document.getElementById(`g-${glossSlug(hit.term)}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 60)
    const t2 = setTimeout(() => setFlash(''), 2600)
    return () => { clearTimeout(t); clearTimeout(t2) }
  }, [want])

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-muted-foreground">
        Every piece of restaurant jargon used in RestoBOXX, in plain English —
        with a pointer to where you’ll meet it. Dotted-underlined terms anywhere in this manual jump
        straight here.
      </p>
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <div className="flex min-w-56 items-center gap-2 rounded-lg border bg-card px-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter terms…"
            className="w-full bg-transparent py-2 text-sm outline-none" />
          {filter && <button onClick={() => setFilter('')}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>}
        </div>
        <span className="text-xs text-muted-foreground">{entries.length} terms</span>
      </div>
      <div className="flex flex-wrap gap-1 print:hidden">
        {byLetter.map(([l]) => (
          <button key={l}
            onClick={() => document.getElementById(`gl-${l}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
            className="rounded border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:border-primary hover:text-foreground">
            {l}
          </button>
        ))}
      </div>
      {byLetter.map(([l, list]) => (
        <div key={l} id={`gl-${l}`}>
          <div className="mb-2 mt-2 text-sm font-bold text-primary">{l}</div>
          <div className="space-y-3">
            {list.map((e) => (
              <div key={e.term} id={`g-${glossSlug(e.term)}`}
                className={cn('rounded-lg border bg-card p-3 transition-shadow',
                  flash === e.term && 'border-primary ring-2 ring-primary/30')}>
                <div className="text-sm font-semibold text-foreground">
                  {e.term}
                  {e.aka && <span className="ml-2 text-xs font-normal text-muted-foreground">also: {e.aka}</span>}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{e.def}</p>
                {e.where && <p className="mt-1 text-xs text-muted-foreground/70">In RestoBOXX: {e.where}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/* ── search across topics + glossary ────────────────────────────────────── */

function useHelpSearch(q) {
  return useMemo(() => {
    const needle = q.toLowerCase().trim()
    if (!needle) return null
    const topics = ALL_TOPICS
      .map((t) => {
        const hay = `${t.title} ${t.summary} ${t.keywords}`.toLowerCase()
        const i = hay.indexOf(needle)
        return { t, s: i < 0 ? -1 : 100 - i }
      })
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.t)
    const gloss = GLOSSARY
      .filter((e) => `${e.term} ${e.aka || ''} ${e.def}`.toLowerCase().includes(needle))
      .slice(0, 12)
    return { topics, gloss }
  }, [q])
}

/* ── the page ───────────────────────────────────────────────────────────── */

export default function HelpCenter() {
  const { topicId } = useParams()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [printAll, setPrintAll] = useState(false)
  const topRef = useRef(null)

  const topic = topicById(topicId) ?? topicById('welcome')
  const flat = HELP_GROUPS.flatMap((g) => g.topics)
  const idx = flat.findIndex((t) => t.id === topic.id)
  const prev = idx > 0 ? flat[idx - 1] : null
  const next = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null
  const groupOf = (t) => HELP_GROUPS.find((g) => g.topics.some((x) => x.id === t.id))?.label || ''

  const results = useHelpSearch(q)

  useEffect(() => { setQ(''); topRef.current?.scrollIntoView({ block: 'start' }) }, [topic.id])

  // Print the whole manual: render everything, give the layout a moment, print, restore.
  useEffect(() => {
    if (!printAll) return
    const t = setTimeout(() => window.print(), 800)
    const done = () => setPrintAll(false)
    window.addEventListener('afterprint', done)
    return () => { clearTimeout(t); window.removeEventListener('afterprint', done) }
  }, [printAll])

  const renderBody = (t) => (t.id === 'glossary' ? <GlossaryView /> : <t.Body />)

  if (printAll) {
    return (
      <div className="mx-auto max-w-3xl space-y-10">
        <div className="print:hidden rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-700 dark:text-sky-300">
          Preparing the full manual for printing… <button className="font-semibold underline" onClick={() => setPrintAll(false)}>cancel</button>
        </div>
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">RestoBOXX-InTutor</h1>
          <p className="mt-1 text-sm text-muted-foreground">The complete platform manual, tutorials and business glossary.</p>
        </div>
        {HELP_GROUPS.map((g) => (
          <div key={g.label} className="space-y-8">
            <h2 className="border-b pb-1 text-xl font-bold text-foreground">{g.label}</h2>
            {g.topics.map((t) => (
              <section key={t.id} style={{ breakInside: 'avoid-page' }}>
                <h3 className="mb-1 text-lg font-semibold text-foreground">{t.title}</h3>
                <p className="mb-3 text-sm text-muted-foreground">{t.summary}</p>
                {renderBody(t)}
              </section>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div ref={topRef} className="w-full">
      {/* header */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <GraduationCap className="h-4 w-4 text-primary" /> Help Center
          </div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">RestoBOXX-InTutor</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            The full RestoBOXX manual — every screen, step-by-step tutorials and the restaurant glossary.
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
            <Printer className="h-4 w-4" /> Print topic
          </button>
          <button onClick={() => setPrintAll(true)}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
            <BookOpen className="h-4 w-4" /> Print full manual
          </button>
        </div>
      </div>

      {/* search */}
      <div className="mb-5 print:hidden">
        <div className="flex items-center gap-2 rounded-xl border bg-card px-3 shadow-sm focus-within:border-primary">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search the manual & glossary…  (e.g. “menu”, “table QR”, “reservation”)"
            className="w-full bg-transparent py-2.5 text-sm outline-none" />
          {q && <button onClick={() => setQ('')}><X className="h-4 w-4 text-muted-foreground" /></button>}
        </div>
      </div>

      {results ? (
        /* ── search results ── */
        <div className="space-y-6">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Topics ({results.topics.length})
            </div>
            {results.topics.length === 0 && <p className="text-sm text-muted-foreground">No matching topics.</p>}
            <div className="grid gap-2 md:grid-cols-2">
              {results.topics.map((t) => (
                <button key={t.id} onClick={() => { setQ(''); navigate(`/help/${t.id}`) }}
                  className="rounded-xl border bg-card p-3 text-left shadow-sm transition hover:border-primary">
                  <div className="text-sm font-semibold text-foreground">{t.title}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{t.summary}</div>
                  <div className="mt-1.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {groupOf(t)}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Glossary ({results.gloss.length})
            </div>
            {results.gloss.length === 0 && <p className="text-sm text-muted-foreground">No matching terms.</p>}
            <div className="space-y-2">
              {results.gloss.map((e) => (
                <button key={e.term}
                  onClick={() => { setQ(''); navigate(`/help/glossary?term=${encodeURIComponent(e.term)}`) }}
                  className="block w-full rounded-xl border bg-card p-3 text-left shadow-sm transition hover:border-primary">
                  <span className="text-sm font-semibold text-foreground">{e.term}</span>
                  {e.aka && <span className="ml-2 text-xs text-muted-foreground">({e.aka})</span>}
                  <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{e.def}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* ── TOC + topic ── */
        <div className="flex gap-6">
          <nav className="hidden w-60 shrink-0 lg:block print:hidden">
            <div className="sticky top-0 max-h-[calc(100vh-9rem)] space-y-4 overflow-y-auto pb-6">
              {HELP_GROUPS.map((g) => (
                <div key={g.label}>
                  <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                    {g.label}
                  </div>
                  {g.topics.map((t) => (
                    <Link key={t.id} to={`/help/${t.id}`}
                      className={cn('block truncate rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground',
                        t.id === topic.id && 'bg-primary/10 font-medium text-foreground')}>
                      {t.title}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </nav>

          <article className="min-w-0 flex-1">
            <div className="rounded-xl border bg-card p-6 shadow-sm">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                {groupOf(topic)}
              </div>
              <h2 className="mb-1 text-xl font-semibold text-foreground">{topic.title}</h2>
              <p className="mb-5 text-sm text-muted-foreground">{topic.summary}</p>
              {renderBody(topic)}
            </div>

            <div className="mt-4 flex items-center justify-between print:hidden">
              {prev ? (
                <Link to={`/help/${prev.id}`}
                  className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm text-muted-foreground hover:border-primary hover:text-foreground">
                  <ChevronLeft className="h-4 w-4" /> {prev.title}
                </Link>
              ) : <span />}
              {next ? (
                <Link to={`/help/${next.id}`}
                  className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm text-muted-foreground hover:border-primary hover:text-foreground">
                  {next.title} <ChevronRight className="h-4 w-4" />
                </Link>
              ) : <span />}
            </div>
          </article>
        </div>
      )}
    </div>
  )
}
